/**
 * `/entrar` (SPEC-v2 2.2, `SUI-01`). No bundle inicial e pequena: não baixa nenhuma área. Quando o
 * campo de senha recebe foco, o app começa a baixar a área de destino (Painel, na maioria).
 */
import type { AuthProvider, Ms } from '@rota/contracts';
import { useEffect, useState } from 'react';
import { useLocation, useSearch } from 'wouter';
import { suiteCopy } from '@/components/suite/copy';
import { LoginCard, type RememberedAccount } from '@/components/suite/LoginCard';
import { sessionStore, useSessionState } from '@/data/auth/session';
import { getSuite } from '@/data/provider';
import { preloadArea } from '../suite/preload';
import { bootstrapSession } from '../suite/session';
import { AuthLayout } from './AuthLayout';
import { authCopy } from './copy';
import { destinationAfterLogin, setChallenge } from './flow';

type MockExtras = { rememberedAccounts?(): Promise<RememberedAccount[]> };

export function LoginPage() {
  const [, navigate] = useLocation();
  const search = useSearch();
  const volta = new URLSearchParams(search).get('volta');
  const st = useSessionState();
  const [remembered, setRemembered] = useState<RememberedAccount[] | null>(null);
  const [error, setError] = useState<string | undefined>();
  const [retryAt, setRetryAt] = useState<Ms | undefined>();
  const [attempt, setAttempt] = useState(0);
  const auth = getSuite().auth as AuthProvider & MockExtras;

  useEffect(() => {
    if (st.status === 'unknown') void bootstrapSession();
  }, [st.status]);

  // Já com sessão (outra aba, "Manter conectado"): segue direto.
  useEffect(() => {
    if (st.status === 'signed_in' && st.session)
      navigate(destinationAfterLogin(st.session, volta), { replace: true });
    else if (st.status === 'signed_out' && st.needsSetup) navigate('/configurar', { replace: true });
  }, [st, navigate, volta]);

  useEffect(() => {
    let live = true;
    void (auth.rememberedAccounts?.() ?? Promise.resolve([])).then((r) => live && setRemembered(r));
    return () => {
      live = false;
    };
  }, [auth]);

  async function onSubmit(i: { email: string; password: string; remember: boolean }) {
    setError(undefined);
    const r = await auth.signIn(i);
    switch (r.status) {
      case 'ok':
        sessionStore.signedIn(r.session);
        navigate(destinationAfterLogin(r.session, volta), { replace: true });
        return;
      case 'totp_required':
        setChallenge(r.challengeId, volta);
        navigate('/entrar/codigo');
        return;
      case 'setup_required':
        navigate('/configurar', { replace: true });
        return;
      case 'throttled':
        setRetryAt(r.retryAt);
        setError(suiteCopy.login.error);
        setAttempt((n) => n + 1);
        return;
      case 'invalid':
        // Nova referência a cada erro: a tremida e o foco repetem mesmo com o mesmo texto.
        setError(
          `${suiteCopy.login.error}​`.slice(0, suiteCopy.login.error.length) +
            (error === suiteCopy.login.error ? '​' : ''),
        );
        return;
    }
  }

  return (
    <AuthLayout title={authCopy.login.title} subtitle={authCopy.login.subtitle} page={authCopy.login.page}>
      {remembered && (
        <LoginCard
          remembered={remembered}
          onSubmit={onSubmit}
          error={error}
          errorNonce={attempt}
          retryAt={retryAt}
          onForgot={() => navigate('/esqueci')}
          onPasswordFocus={() => preloadArea('painel')}
        />
      )}
    </AuthLayout>
  );
}
